import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

const PRIMARY = "#292E5C";
const SECONDARY = "#F7AA47";
const DARK_BLACK = "#000000";
const TEXT_DARK = "#171717";
const PLACEHOLDER = "#667085";
const BORDER = "#D8DBDF";
const ERROR = "#D92D20";

const BOARD_OPTIONS = [
  { value: "CBSE", label: "CBSE" },
  { value: "ICSE", label: "ICSE" },
  { value: "State", label: "State" },
];

const STATUS_OPTIONS = [
  { value: "pilot", label: "Pilot" },
  { value: "paid", label: "Paid" },
];

const EMPTY_FORM = {
  school_name: "",
  board: "",
  city: "",
  pincode: "",
  status: "pilot",
  school_gmail_id: "",
  school_contact_number: "",
};

const createEmptyForm = () => ({ ...EMPTY_FORM });

const isPincodeValid = (value) => /^\d{6}$/.test(value);

const isEmailValid = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const normalizeBoardValue = (value) => {
  const board = String(value || "").trim().toLowerCase();
  const match = BOARD_OPTIONS.find(
    (option) =>
      option.value.toLowerCase() === board || option.label.toLowerCase() === board
  );

  return match?.value || "";
};

const validateForm = (form) => {
  const errors = {};

  [
    "school_name",
    "board",
    "city",
    "pincode",
    "status",
    "school_gmail_id",
    "school_contact_number",
  ].forEach((field) => {
    if (!String(form[field] || "").trim()) {
      errors[field] = "This field is required.";
    }
  });

  if (form.school_gmail_id && !isEmailValid(form.school_gmail_id)) {
    errors.school_gmail_id = "Enter a valid school Gmail Id.";
  }

  if (form.pincode && !isPincodeValid(form.pincode)) {
    errors.pincode = "Enter a 6 digit pin code.";
  }

  if (
    form.status &&
    !STATUS_OPTIONS.some((option) => option.value === form.status)
  ) {
    errors.status = "Select a valid status.";
  }

  if (form.board && !BOARD_OPTIONS.some((option) => option.value === form.board)) {
    errors.board = "Select a valid board.";
  }

  return errors;
};

const buildPayload = (form) => ({
  school_name: form.school_name.trim(),
  board: form.board.trim(),
  city: form.city.trim(),
  pincode: form.pincode.trim(),
  status: form.status,
  school_gmail_id: form.school_gmail_id.trim(),
  school_contact_number: form.school_contact_number.trim(),
});

const normalizeStatusValue = (value) => {
  const status = String(value || "").trim().toLowerCase();
  return STATUS_OPTIONS.some((option) => option.value === status) ? status : "pilot";
};

const createFormFromValues = (values) => {
  if (!values) {
    return createEmptyForm();
  }

  return {
    school_name: values.school_name || values.name || "",
    board: normalizeBoardValue(values.board || values.board_id),
    city: values.city || "",
    pincode: values.pincode || "",
    status: normalizeStatusValue(values.status_value || values.status),
    school_gmail_id: values.school_gmail_id || "",
    school_contact_number: values.school_contact_number || "",
  };
};

const labelSx = {
  color: DARK_BLACK,
  fontSize: 15,
  fontFamily: "Poppins",
  fontWeight: 400,
  lineHeight: "20px",
  mb: "5px",
};

const requiredMarkSx = {
  color: ERROR,
  fontSize: 15,
  fontFamily: "Poppins",
  fontWeight: 400,
};

const helperTextSx = {
  mt: "2px",
  color: ERROR,
  fontSize: 10,
  fontFamily: "Poppins",
  lineHeight: "12px",
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    height: 42,
    bgcolor: "#FFFFFF",
    borderRadius: "5px",
    color: TEXT_DARK,
    fontSize: 15,
    fontFamily: "Poppins",
    fontWeight: 400,
    lineHeight: "20px",

    "& fieldset": {
      borderColor: BORDER,
      borderWidth: "1px",
    },

    "&:hover fieldset": {
      borderColor: PRIMARY,
    },

    "&.Mui-focused fieldset": {
      borderColor: PRIMARY,
      borderWidth: "1px",
    },

    "&.Mui-error fieldset": {
      borderColor: ERROR,
    },

    "& input": {
      px: "12px",
      py: "10px",

      "&::placeholder": {
        color: PLACEHOLDER,
        opacity: 1,
      },
    },
  },

  "& .MuiFormHelperText-root": {
    mx: 0,
    mt: "2px",
    fontSize: 10,
    fontFamily: "Poppins",
    lineHeight: "12px",
  },
};

const selectSx = {
  height: 42,
  bgcolor: "#FFFFFF",
  borderRadius: "5px",
  color: TEXT_DARK,
  fontSize: 14,
  fontFamily: "Poppins",
  fontWeight: 400,
  lineHeight: "20px",

  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: BORDER,
  },

  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: PRIMARY,
  },

  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: PRIMARY,
    borderWidth: "1px",
  },

  "&.Mui-error .MuiOutlinedInput-notchedOutline": {
    borderColor: ERROR,
  },

  "& .MuiSelect-select": {
    py: "10px",
    px: "12px",
  },
};

const menuProps = {
  PaperProps: {
    sx: {
      mt: 0.5,
      borderRadius: "5px",
      boxShadow: "0px 2px 6px rgba(16, 24, 40, 0.06)",
      maxHeight: 240,

      "& .MuiMenuItem-root": {
        minHeight: 24,
        px: "17px",
        py: "2px",
        color: "#111827",
        fontSize: 11,
        fontFamily: "Montserrat",
        fontWeight: 500,
        lineHeight: "20px",
      },

      "& .Mui-selected": {
        bgcolor: "#F2F2F2 !important",
      },
    },
  },
};

const AddSchoolModal = ({
  open,
  onClose,
  onSubmit,
  submitting = false,
  initialValues = null,
  title = "Add a School",
  submitLabel = "Add",
}) => {
  const [form, setForm] = useState(createEmptyForm);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    setForm(createFormFromValues(initialValues));
    setErrors({});
    setSubmitError("");
  }, [initialValues, open]);

  const setFieldValue = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));

    setErrors((current) => ({
      ...current,
      [field]: "",
    }));

    setSubmitError("");
  };

  const handlePincodeChange = (event) => {
    setFieldValue("pincode", event.target.value.replace(/\D/g, "").slice(0, 6));
  };

  const handleClose = () => {
    if (!submitting) {
      onClose();
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const nextErrors = validateForm(form);
    setErrors(nextErrors);

    if (Object.values(nextErrors).some(Boolean)) {
      return;
    }

    try {
      const result = await onSubmit(buildPayload(form));

      if (result?.ok) {
        onClose();
        return;
      }

      setSubmitError(result?.message || "Unable to add school right now.");
    } catch (error) {
      setSubmitError(
        error?.message || "Something went wrong while adding school."
      );
    }
  };

  const renderPlaceholder = (placeholder) => (
    <Typography
      component="span"
      sx={{
        color: PLACEHOLDER,
        fontSize: 15,
        fontFamily: "Poppins",
        fontWeight: 400,
        lineHeight: "20px",
      }}
    >
      {placeholder}
    </Typography>
  );

  const renderBoardValue = (value) => {
    if (!value) {
      return renderPlaceholder("Select board");
    }

    return (
      BOARD_OPTIONS.find((option) => option.value === value)?.label || value
    );
  };

  const renderStatusValue = (value) => {
    if (!value) {
      return renderPlaceholder("Select status");
    }

    return STATUS_OPTIONS.find((option) => option.value === value)?.label || value;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth={false}
      PaperProps={{
        component: "form",
        onSubmit: handleSubmit,
        sx: {
          width: 716,
          maxWidth: "calc(100vw - 32px)",
          maxHeight: "92vh",
          bgcolor: "#FFFFFF",
          borderRadius: "15px",
          boxShadow: "0px 16px 75px rgba(0, 0, 0, 0.04)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle
        sx={{
          minHeight: 40,
          px: "15px",
          py: "8px",
          bgcolor: PRIMARY,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexShrink: 0,
        }}
      >
        <Typography
          sx={{
            color: "#FFFFFF",
            fontSize: 15,
            fontFamily: "Montserrat",
            fontWeight: 600,
            wordBreak: "break-word",
          }}
        >
          {title}
        </Typography>

        <IconButton
          type="button"
          aria-label="Close add school modal"
          disabled={submitting}
          onClick={handleClose}
          sx={{
            width: 32,
            height: 32,
            color: "#FFFFFF",
            p: 0,

            "&:hover": {
              bgcolor: "rgba(255,255,255,0.12)",
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 22 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
         px: "24px",
          pt: "18px",
          pb: "18px",
          mt:"20px",
          bgcolor: "#FFFFFF",
          overflowY: "auto",

          "&::-webkit-scrollbar": {
            width: 5,
          },

          "&::-webkit-scrollbar-thumb": {
            bgcolor: "rgba(0,0,0,0.28)",
            borderRadius: 10,
          },
        }}
      >
        {submitError ? (
          <Alert
            severity="error"
            sx={{
              mb: 2,
              borderRadius: "8px",
              fontFamily: "Poppins",
            }}
          >
            {submitError}
          </Alert>
        ) : null}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
            },
            gap: "10px",
            alignItems: "flex-start",
          }}
        >
          <Box>
            <Typography sx={labelSx}>
              School Name <Box component="span" sx={requiredMarkSx}>*</Box>
            </Typography>

            <TextField
              fullWidth
              required
              value={form.school_name}
              disabled={submitting}
              placeholder="Enter school name"
              autoComplete="organization"
              error={Boolean(errors.school_name)}
              helperText={errors.school_name || ""}
              onChange={(event) => setFieldValue("school_name", event.target.value)}
              sx={inputSx}
            />
          </Box>

          <Box>
            <Typography sx={labelSx}>
              School Gmail Id <Box component="span" sx={requiredMarkSx}>*</Box>
            </Typography>

            <TextField
              fullWidth
              required
              type="email"
              value={form.school_gmail_id}
              disabled={submitting}
              placeholder="Enter school Gmail Id"
              autoComplete="email"
              error={Boolean(errors.school_gmail_id)}
              helperText={errors.school_gmail_id || ""}
              onChange={(event) =>
                setFieldValue("school_gmail_id", event.target.value)
              }
              sx={inputSx}
            />
          </Box>

          <Box>
            <Typography sx={labelSx}>
              School Contact number <Box component="span" sx={requiredMarkSx}>*</Box>
            </Typography>

            <TextField
              fullWidth
              required
              value={form.school_contact_number}
              disabled={submitting}
              placeholder="Enter school contact number"
              autoComplete="tel"
              error={Boolean(errors.school_contact_number)}
              helperText={errors.school_contact_number || ""}
              onChange={(event) =>
                setFieldValue("school_contact_number", event.target.value)
              }
              sx={inputSx}
            />
          </Box>

          <Box>
            <Typography sx={labelSx}>
              Board <Box component="span" sx={requiredMarkSx}>*</Box>
            </Typography>

            <Select
              fullWidth
              displayEmpty
              required
              value={form.board}
              disabled={submitting}
              error={Boolean(errors.board)}
              IconComponent={KeyboardArrowDownIcon}
              onChange={(event) => setFieldValue("board", event.target.value)}
              renderValue={renderBoardValue}
              MenuProps={menuProps}
              sx={selectSx}
            >
              {BOARD_OPTIONS.map((board) => (
                <MenuItem key={board.value} value={board.value}>
                  {board.label}
                </MenuItem>
              ))}
            </Select>

            {errors.board ? <Typography sx={helperTextSx}>{errors.board}</Typography> : null}
          </Box>

          <Box>
            <Typography sx={labelSx}>
              City <Box component="span" sx={requiredMarkSx}>*</Box>
            </Typography>

            <TextField
              fullWidth
              required
              value={form.city}
              disabled={submitting}
              placeholder="Enter city"
              autoComplete="address-level2"
              error={Boolean(errors.city)}
              helperText={errors.city || ""}
              onChange={(event) => setFieldValue("city", event.target.value)}
              sx={inputSx}
            />
          </Box>

          <Box>
            <Typography sx={labelSx}>
              Pin Code <Box component="span" sx={requiredMarkSx}>*</Box>
            </Typography>

            <TextField
              fullWidth
              required
              value={form.pincode}
              disabled={submitting}
              placeholder="Enter pin code"
              autoComplete="postal-code"
              error={Boolean(errors.pincode)}
              helperText={errors.pincode || ""}
              onChange={handlePincodeChange}
              inputProps={{
                inputMode: "numeric",
                maxLength: 6,
              }}
              sx={inputSx}
            />
          </Box>

          <Box>
            <Typography sx={labelSx}>
              Status <Box component="span" sx={requiredMarkSx}>*</Box>
            </Typography>

            <Select
              fullWidth
              required
              value={form.status}
              disabled={submitting}
              error={Boolean(errors.status)}
              IconComponent={KeyboardArrowDownIcon}
              onChange={(event) => setFieldValue("status", event.target.value)}
              renderValue={renderStatusValue}
              MenuProps={menuProps}
              sx={selectSx}
            >
              {STATUS_OPTIONS.map((status) => (
                <MenuItem key={status.value} value={status.value}>
                  {status.label}
                </MenuItem>
              ))}
            </Select>

            {errors.status ? (
              <Typography sx={helperTextSx}>{errors.status}</Typography>
            ) : null}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: "40px",
          pt: "20px",
          pb: "30px",
          bgcolor: "#FFFFFF",
          borderTop: "1px solid #E0E0E0",
          display: "flex",
          justifyContent: "flex-end",
          gap: "8px",
          flexShrink: 0,
        }}
      >
        <Button
          type="button"
          disabled={submitting}
          onClick={handleClose}
          variant="outlined"
          sx={{
            px: "30px",
            py: "8px",
            minWidth: 108,
            borderRadius: "7px",
            borderColor: "#B5B5B5",
            color: "#B5B5B5",
            fontSize: 16,
            fontFamily: "Poppins",
            fontWeight: 500,
            textTransform: "none",
            lineHeight: "normal",

            "&:hover": {
              borderColor: "#B5B5B5",
              bgcolor: "rgba(181,181,181,0.06)",
            },
          }}
        >
          Cancel
        </Button>

        <Button
          type="submit"
          disabled={submitting}
          variant="contained"
          sx={{
            width: 108,
            px: "30px",
            py: "8px",
            borderRadius: "7px",
            bgcolor: SECONDARY,
            color: "#FDFDFD",
            fontSize: 16,
            fontFamily: "Poppins",
            fontWeight: 500,
            textTransform: "none",
            lineHeight: "normal",
            boxShadow: "none",

            "&:hover": {
              bgcolor: "#E99A32",
              boxShadow: "none",
            },

            "&.Mui-disabled": {
              bgcolor: "rgba(247, 170, 71, 0.55)",
              color: "#FFFFFF",
            },
          }}
        >
          {submitting ? "Saving..." : submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

AddSchoolModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  submitting: PropTypes.bool,
  initialValues: PropTypes.shape({
    school_name: PropTypes.string,
    name: PropTypes.string,
    board: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    board_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    city: PropTypes.string,
    pincode: PropTypes.string,
    status: PropTypes.string,
    status_value: PropTypes.string,
    school_gmail_id: PropTypes.string,
    school_contact_number: PropTypes.string,
  }),
  title: PropTypes.string,
  submitLabel: PropTypes.string,
};

export default AddSchoolModal;
